// utils/imageUtils.ts
//
// SVG-based blur placeholders for next/image `blurDataURL`. The pre-encoded
// constants are the most performant; the generators give you per-call sizing.

/**
 * Generate a light grey gradient blur placeholder.
 */
export const generateLightGreyBlur = (
  width: number = 550,
  height: number = 310,
): string => {
  const svg = `
    <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#f9fafb"/>
          <stop offset="50%" stop-color="#f3f4f6"/>
          <stop offset="100%" stop-color="#e5e7eb"/>
        </linearGradient>
      </defs>
      <rect width="100%" height="100%" fill="url(#grad)"/>
      <rect x="20%" y="40%" width="60%" height="20%" fill="#dddddd" rx="4"/>
    </svg>
  `;

  return `data:image/svg+xml;base64,${Buffer.from(svg).toString("base64")}`;
};

/**
 * Generate a shimmer effect placeholder.
 */
export const generateShimmerBlur = (
  width: number = 550,
  height: number = 310,
): string => {
  const svg = `
    <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="shimmer" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stop-color="#f1f3f4" stop-opacity="0.8"/>
          <stop offset="50%" stop-color="#ffffff" stop-opacity="0.6"/>
          <stop offset="100%" stop-color="#f1f3f4" stop-opacity="0.8"/>
          <animateTransform
            attributeName="gradientTransform"
            type="translate"
            values="-100 0;100 0;-100 0"
            dur="2s"
            repeatCount="indefinite"/>
        </linearGradient>
      </defs>
      <rect width="100%" height="100%" fill="#f8f9fa"/>
      <rect width="100%" height="100%" fill="url(#shimmer)"/>
      <rect x="20%" y="70%" width="60%" height="8%" fill="#e9ecef" rx="4"/>
      <rect x="20%" y="80%" width="40%" height="6%" fill="#e9ecef" rx="3"/>
    </svg>
  `;

  return `data:image/svg+xml;base64,${Buffer.from(svg).toString("base64")}`;
};

/**
 * Generate a card-like placeholder that matches your design.
 */
export const generateCardPlaceholder = (
  width: number = 550,
  height: number = 310,
): string => {
  const svg = `
    <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="bg" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stop-color="#ffffff"/>
          <stop offset="100%" stop-color="#f8fafc"/>
        </linearGradient>
      </defs>
      <!-- Card background -->
      <rect width="100%" height="100%" fill="url(#bg)" rx="8"/>

      <!-- Image placeholder area -->
      <rect x="0" y="0" width="100%" height="60%" fill="#e2e8f0" rx="8"/>

      <!-- Image icon placeholder -->
      <circle cx="50%" cy="30%" r="20" fill="#cbd5e1"/>
      <path d="M ${width / 2 - 8} ${height * 0.3 - 4} l16 0 l-8 8 z" fill="#ffffff"/>

      <!-- Text content area -->
      <rect x="8%" y="68%" width="70%" height="6%" fill="#e2e8f0" rx="3"/>
      <rect x="8%" y="78%" width="50%" height="4%" fill="#f1f5f9" rx="2"/>
      <rect x="8%" y="86%" width="60%" height="4%" fill="#f1f5f9" rx="2"/>
    </svg>
  `;

  return `data:image/svg+xml;base64,${Buffer.from(svg).toString("base64")}`;
};

/**
 * Pre-encoded light grey placeholder. Most performant.
 */
export const LIGHT_GREY_BLUR =
  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTUwIiBoZWlnaHQ9IjMxMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8ZGVmcz4KICAgIDxsaW5lYXJHcmFkaWVudCBpZD0iZyIgeDE9IjAlIiB5MT0iMCUiIHgyPSIxMDAlIiB5Mj0iMTAwJSI+CiAgICAgIDxzdG9wIG9mZnNldD0iMCUiIHN0b3AtY29sb3I9IiNmOWZhZmIiLz4KICAgICAgPHN0b3Agb2Zmc2V0PSI1MCUiIHN0b3AtY29sb3I9IiNmM2Y0ZjYiLz4KICAgICAgPHN0b3Agb2Zmc2V0PSIxMDAlIiBzdG9wLWNvbG9yPSIjZTVlN2ViIi8+CiAgICA8L2xpbmVhckdyYWRpZW50PgogIDwvZGVmcz4KICA8cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2cpIi8+CiAgPHJlY3QgeD0iMjAlIiB5PSI0MCUiIHdpZHRoPSI2MCUiIGhlaWdodD0iMjAlIiBmaWxsPSIjZGRkZGRkIiByeD0iNCIvPgo8L3N2Zz4=";

/**
 * Brand-specific Ecolab colors placeholder.
 */
export const ECOLAB_PLACEHOLDER =
  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTUwIiBoZWlnaHQ9IjMxMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8ZGVmcz4KICAgIDxsaW5lYXJHcmFkaWVudCBpZD0iZWNvIiB4MT0iMCUiIHkxPSIwJSIgeDI9IjEwMCUiIHkyPSIxMDAlIj4KICAgICAgPHN0b3Agb2Zmc2V0PSIwJSIgc3RvcC1jb2xvcj0iI2Y0ZjhmYiIvPgogICAgICA8c3RvcCBvZmZzZXQ9IjUwJSIgc3RvcC1jb2xvcj0iI2VkZjJmNyIvPgogICAgICA8c3RvcCBvZmZzZXQ9IjEwMCUiIHN0b3AtY29sb3I9IiNkZGY0ZmYiLz4KICAgIDwvbGluZWFyR3JhZGllbnQ+CiAgPC9kZWZzPgogIDxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZWNvKSIvPgogIDxyZWN0IHg9IjIwJSIgeT0iNzAlIiB3aWR0aD0iNjAlIiBoZWlnaHQ9IjglIiBmaWxsPSIjYzNkZGZkIiByeD0iNCIvPgogIDxyZWN0IHg9IjIwJSIgeT0iODAlIiB3aWR0aD0iNDAlIiBoZWlnaHQ9IjYlIiBmaWxsPSIjZGRlZmZmIiByeD0iMyIvPgo8L3N2Zz4=";
